import React from "react";

const FOCUSABLE_ELEMENT_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable="true"]',
].join(",");

function getFocusableElements(container: HTMLElement): HTMLElement[] {
  return Array.from(
    container.querySelectorAll<HTMLElement>(FOCUSABLE_ELEMENT_SELECTOR)
  ).filter(
    (focusableElement) =>
      !focusableElement.hidden &&
      focusableElement.tabIndex >= 0 &&
      focusableElement.getAttribute("aria-hidden") !== "true"
  );
}

interface UseModalFocusTrapOptions {
  closeDisabled?: boolean;
  dialogRef: React.RefObject<HTMLDivElement | null>;
  isOpen: boolean;
  onClose: () => void;
}

export function useModalFocusTrap({
  closeDisabled = false,
  dialogRef,
  isOpen,
  onClose,
}: UseModalFocusTrapOptions): void {
  const onCloseRef = React.useRef(onClose);
  const closeDisabledRef = React.useRef(closeDisabled);

  onCloseRef.current = onClose;
  closeDisabledRef.current = closeDisabled;

  React.useEffect(() => {
    if (!isOpen) return undefined;

    const previouslyFocusedElement =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const dialog = dialogRef.current;
    if (!dialog) return undefined;

    const initialFocusableElement = getFocusableElements(dialog)[0] ?? dialog;
    initialFocusableElement.focus();

    const handleKeyDown = (keyboardEvent: KeyboardEvent) => {
      const openDialogs = Array.from(
        document.querySelectorAll<HTMLElement>('[role="dialog"][aria-modal="true"]')
      );
      if (openDialogs[openDialogs.length - 1] !== dialog) return;

      if (keyboardEvent.key === "Escape") {
        keyboardEvent.preventDefault();
        if (!closeDisabledRef.current) onCloseRef.current();
        return;
      }
      if (keyboardEvent.key !== "Tab") return;

      const currentFocusableElements = getFocusableElements(dialog);
      if (currentFocusableElements.length === 0) {
        keyboardEvent.preventDefault();
        dialog.focus();
        return;
      }

      const activeElementIndex = currentFocusableElements.indexOf(
        document.activeElement as HTMLElement
      );
      const firstFocusableElement = currentFocusableElements[0];
      const lastFocusableElement =
        currentFocusableElements[currentFocusableElements.length - 1];

      if (
        keyboardEvent.shiftKey &&
        (activeElementIndex <= 0 || !dialog.contains(document.activeElement))
      ) {
        keyboardEvent.preventDefault();
        lastFocusableElement.focus();
        return;
      }
      if (
        !keyboardEvent.shiftKey &&
        (activeElementIndex === -1 ||
          activeElementIndex === currentFocusableElements.length - 1)
      ) {
        keyboardEvent.preventDefault();
        firstFocusableElement.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      if (previouslyFocusedElement?.isConnected) previouslyFocusedElement.focus();
    };
  }, [dialogRef, isOpen]);
}
